import { createSelector } from "@reduxjs/toolkit";
import { isEqual } from "es-toolkit";
import { useCallback, useMemo, type ReactNode } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  addConditionAction,
  addEffectAction,
  conditionsStateSelector,
  effectsByIdSelector,
  effectsStateSelector,
  getConditionBuildCreate,
  getNextIdSelector,
  getNodeSlotIdSelector,
  queryEffectBuildsCreate,
  removeEffectAction,
  reorderConditionAction,
  reorderEffectAction,
  updateConditionAction,
  updateEffectAction,
  type AddConditionAction,
  type ConditionBuild,
  type ConditionSlotId,
  type EffectBuild,
} from "@skilitics/nautilus-core-designer-data";

import { getDesignerNodeByIdSelector } from "../state/contextualSelectors";
import type { EffectBuildable } from "../types";
import { createConditionCreate, getConditionCreate } from "./conditions";
import { addEffectValidatedCreate, createEffectCreate, getEffectBuildableCreate, getEffectsCreate } from "./effects";
import { resolveAddableEffectBuildablesCreate } from "./querying";
import { ResponseDesignerContext } from "./ResponseDesignerContext";
import { useTriggerDesigns } from "./TriggerDesignsContext";

type ProviderProps = {
  children: Exclude<ReactNode, null | undefined | boolean>;

  /** generally available effect builders (not node specific) e.g. locale */
  coreEffectBuildables: ReadonlyArray<Omit<EffectBuildable, "nodeId">>;
};

const queryEffectBuildsSelector = createSelector(effectsStateSelector, (effectsState) =>
  queryEffectBuildsCreate({ effectsState }),
);

export const ResponseDesignerProvider = ({ children, coreEffectBuildables }: ProviderProps) => {
  const dispatch = useDispatch();
  const getById = useSelector(getDesignerNodeByIdSelector);
  const getSlotId = useSelector(getNodeSlotIdSelector);
  const getNextId = useSelector(getNextIdSelector);

  const { getEffectBuildables, resolveEffectBuildables, resolveConditionBuildables, getConditionBuildable } =
    useTriggerDesigns();

  const conditionsState = useSelector(conditionsStateSelector);
  const effectsById = useSelector(effectsByIdSelector);

  const queryEffectBuilds = useSelector(queryEffectBuildsSelector);

  const getEffectBuilds = useMemo(
    () => (responseId: number) => queryEffectBuilds({ type: "byResponseId", responseId }),
    [queryEffectBuilds],
  );

  const getEffects = useMemo(
    () => getEffectsCreate(queryEffectBuilds, resolveEffectBuildables),
    [queryEffectBuilds, resolveEffectBuildables],
  );

  const addEffect = useMemo(() => {
    const add = (responseId: number, effect: EffectBuild) => dispatch(addEffectAction({ responseId, effect }));
    return addEffectValidatedCreate(getById, queryEffectBuilds, getEffectBuildables, add);
  }, [dispatch, getById, getEffectBuildables, queryEffectBuilds]);

  const updateEffect = useCallback(
    (effectId: number, config: any) => {
      // as on a condition below: submit-on-blur re-offers an unchanged config
      if (isEqual(effectsById[effectId]?.config, config)) return;
      dispatch(updateEffectAction({ id: effectId, config }));
    },
    [dispatch, effectsById],
  );

  const reorderEffect = useCallback(
    (effectId: number, newIndex: number) => dispatch(reorderEffectAction({ id: effectId, newIndex })),
    [dispatch],
  );

  const removeEffect = useCallback((effectId: number) => dispatch(removeEffectAction({ id: effectId })), [dispatch]);

  const resolveAddableEffectBuildables = useMemo(
    () => resolveAddableEffectBuildablesCreate({ resolveEffectBuildables, queryEffectBuilds, getById, getSlotId }),
    [getById, queryEffectBuilds, getSlotId, resolveEffectBuildables],
  );

  const getEffectBuildable = useMemo(
    () => getEffectBuildableCreate(getEffectBuildables, getById, coreEffectBuildables),
    [getEffectBuildables, getById, coreEffectBuildables],
  );

  const createEffect = useMemo(
    () => createEffectCreate({ getBuildable: getEffectBuildable, getNextId }),
    [getEffectBuildable, getNextId],
  );

  const createCondition = useMemo(
    () => createConditionCreate({ getBuildable: getConditionBuildable, getNextId }),
    [getConditionBuildable, getNextId],
  );

  const addCondition = useCallback(
    (condition: ConditionBuild, to: AddConditionAction["payload"]["to"]) => {
      dispatch(addConditionAction({ to, condition }));
    },
    [dispatch],
  );

  const getCondition = useMemo(
    () =>
      getConditionCreate({
        getConditionBuild: getConditionBuildCreate({ conditionsState }),
        resolveConditionBuildables,
      }),
    [resolveConditionBuildables, conditionsState],
  );

  const updateCondition = useCallback(
    (conditionId: number, config: any) => {
      // A condition form submits on blur, so it re-offers what it already holds
      // every time the author tabs through it. Only a real change is worth a
      // revision — as on a node's own config (see `mutate.update`).
      if (isEqual(conditionsState.byId[conditionId]?.config, config)) return;
      dispatch(updateConditionAction({ id: conditionId, config }));
    },
    [conditionsState, dispatch],
  );

  const reorderCondition = useCallback(
    (conditionId: number, newIndex: number) => dispatch(reorderConditionAction({ id: conditionId, newIndex })),
    [dispatch],
  );

  const moveCondition = useCallback(
    (conditionId: number, newSlotId: ConditionSlotId, newIndex?: number) =>
      dispatch({
        type: "MOVE_CONDITION",
        payload: { id: conditionId, to: { slotId: newSlotId, index: newIndex } },
      }),
    [dispatch],
  );

  const removeCondition = useCallback(
    (conditionId: number) => dispatch({ type: "REMOVE_CONDITION", payload: { id: conditionId } }),
    [dispatch],
  );

  const context = useMemo(
    (): ResponseDesignerContext => ({
      createEffect,
      getEffectBuildable,
      addEffect,
      getEffectBuilds,
      getEffects,
      updateEffect,
      reorderEffect,
      removeEffect,
      resolveAddableEffectBuildables,

      addCondition,
      createCondition,
      getCondition,
      moveCondition,
      removeCondition,
      reorderCondition,
      updateCondition,
    }),
    [
      addCondition,
      addEffect,
      createCondition,
      createEffect,
      getCondition,
      getEffectBuildable,
      getEffectBuilds,
      getEffects,
      moveCondition,
      removeCondition,
      removeEffect,
      reorderCondition,
      reorderEffect,
      resolveAddableEffectBuildables,
      updateCondition,
      updateEffect,
    ],
  );

  return <ResponseDesignerContext.Provider value={context}>{children}</ResponseDesignerContext.Provider>;
};

ResponseDesignerProvider.displayName = "ResponseByIdContext";
